import React, { MouseEvent } from "react"
import {
  Bookmark,
  CheckCircle2,
  ChevronRight,
  Circle,
  FileCheck,
  FileText,
  Printer,
} from "lucide-react"
import toast from "react-hot-toast"
import type { DetailTab, QuestionCardViewModel } from "../types"
import { printQuestionHtml } from "../utils/printQuestion"
import { displayableArtifacts } from "../../../../utils/generatedArtifacts"
import QuestionArtifacts from "../../../../component/questionArtifacts/QuestionArtifacts"
import "./QuestionCard.style.scss"

interface QuestionCardProps {
  card: QuestionCardViewModel
  selected: boolean
  isCompleted?: boolean
  onOpen: (tab?: DetailTab) => void
  onToggleBookmark: (e: MouseEvent) => void
}

const difficultyClass = (difficulty: string) => {
  const d = difficulty.toLowerCase()
  if (d.includes("easy") || d.includes("low")) return "is-easy"
  if (d.includes("hard") || d.includes("high")) return "is-hard"
  return "is-medium"
}

const QuestionCard = ({
  card,
  selected,
  isCompleted,
  onOpen,
  onToggleBookmark,
}: QuestionCardProps) => {
  const topicParts = card.topicPath
    ? card.topicPath.split(/\s*[›>]\s*/).filter(Boolean)
    : []

  const handlePrint = (e: MouseEvent) => {
    e.stopPropagation()
    if (!card.questionHtml?.trim() && !displayableArtifacts(card.questionArtifacts).length) {
      toast.error("Nothing to print for this question")
      return
    }
    const qLabel = card.partLabel ? `Q${card.partLabel}` : "Question"
    printQuestionHtml({
      title: `${card.subjectTitle} — ${qLabel}`,
      html: card.questionHtml,
      artifacts: card.questionArtifacts,
      metaLines: [
        [card.yearLabel, card.typeLabel, card.difficulty]
          .filter((v) => v && v !== "—")
          .join(" · "),
        card.topicPath,
      ],
    })
  }

  return (
    <article
      className={`question-card ${selected ? "is-selected" : ""}`}
      onClick={() => onOpen("attempts")}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onOpen("attempts")
        }
      }}
    >
      <div className="question-card__meta">
        <div className="question-card__chips">
          <span className="question-card__chip question-card__chip--subject">
            {card.subjectTitle}
          </span>
          <span className="question-card__chip">
            {card.yearLabel} · {card.typeLabel}
          </span>
          <span
            className={`question-card__chip question-card__chip--diff ${difficultyClass(card.difficulty)}`}
          >
            {card.difficulty}
          </span>
        </div>
        <div className="question-card__icons">
          <button type="button" aria-label="Print question" onClick={handlePrint}>
            <Printer size={16} />
          </button>
          <button
            type="button"
            className={card.isBookmarked ? "is-on" : undefined}
            aria-label={card.isBookmarked ? "Remove bookmark" : "Bookmark"}
            onClick={onToggleBookmark}
          >
            <Bookmark
              size={16}
              fill={card.isBookmarked ? "currentColor" : "none"}
            />
          </button>
          {isCompleted ? (
            <CheckCircle2 size={18} className="is-done" aria-label="Opened" />
          ) : (
            <Circle size={18} className="is-todo" aria-hidden />
          )}
        </div>
      </div>

      <div className="question-card__body">
        <span className="question-card__snippet-badge">Snippet</span>
        <p className="question-card__snippet">{card.snippet}</p>
        <QuestionArtifacts artifacts={card.questionArtifacts} compact />
        {topicParts.length > 0 && (
          <div className="question-card__topic">
            {topicParts.map((part, i) => (
              <React.Fragment key={`${part}-${i}`}>
                {i > 0 && <ChevronRight size={12} />}
                <span>{part}</span>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      <div className="question-card__footer">
        <button
          type="button"
          className="is-solution"
          disabled={!card.hasSolution}
          onClick={(e) => {
            e.stopPropagation()
            onOpen("solution")
          }}
        >
          <FileText size={14} /> Solution
        </button>
        <button
          type="button"
          className="is-scheme"
          disabled={!card.hasMarkingScheme}
          onClick={(e) => {
            e.stopPropagation()
            onOpen("markscheme")
          }}
        >
          <FileCheck size={14} /> Marking Scheme
        </button>
        <span className="question-card__details">
          Details <ChevronRight size={16} />
        </span>
      </div>
    </article>
  )
}

export default QuestionCard
